import { useEffect, useState } from "react";
import { LinkContainer } from "react-router-bootstrap";
import { Link } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTag,
    faTimes,
    faEdit,
    faTrash,
    faSync,
    faEllipsisVertical,
    faImage,
    faClock,
    faPlay,
    faStar as faSolidStar,
    faStar as faRegStar
} from "@fortawesome/free-solid-svg-icons";
import Stack from "react-bootstrap/Stack";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";

import {
    BooleanBindingModel,
    TagAction,
    TagWithVideoCountViewModel,
    ToggleVideoTagBindingModel,
    VideoLibraryApiClient,
    VideoViewModel
} from "../apiClient";
import { uncategorizedText } from "../constants";
import { confirmAction } from "../helpers/alertHelper";
import { formatDuration } from "../helpers/formatHelper";
import { AddTagModal } from "./AddTagModal";

type Props = {
    video: VideoViewModel;
    tags: TagWithVideoCountViewModel[];
    apiClient: VideoLibraryApiClient;
    onVideoUpdate?: (video: VideoViewModel) => void;
    onVideoToggleTag?: (bindingModel: ToggleVideoTagBindingModel) => void;
}

export const VideoResult = (props: Props) => {
    const { onVideoToggleTag: onToggleTag, video } = props;
    const [updating, setUpdating] = useState<boolean>(false);
    const [thumbnailBase64, setThumbnailBase64] = useState<string | null>(null);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [isFavourite, setIsFavourite] = useState<boolean>(!!video.isFavourite);
    const [imageFailed, setImageFailed] = useState<boolean>(false);
    const [thumbnailLoading, setthumbnailLoading] = useState<boolean>(true);

    useEffect(() => {
        let isMounted = true;
        const loadThumbnail = async () => {
            if (!video.id) {
                if (isMounted) setthumbnailLoading(false);
                return;
            }
            try {
                const thumbnail = await props.apiClient.thumbnail(video.id);
                const blob = (thumbnail as any)?.data ?? thumbnail;
                if (blob instanceof Blob) {
                    const base64data = await new Promise<string>((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            if (reader.result) {
                                resolve(reader.result as string);
                            } else {
                                reject(new Error('Failed to read thumbnail'));
                            }
                        };
                        reader.onerror = () => reject(new Error('FileReader error'));
                        reader.readAsDataURL(blob);
                    });
                    if (isMounted) {
                        setThumbnailBase64(base64data);
                        setthumbnailLoading(false);
                    }
                } else {
                    if (isMounted) {
                        setImageFailed(true);
                        setthumbnailLoading(false);
                    }
                }
            } catch (e) {
                if (isMounted) {
                    setImageFailed(true);
                    setthumbnailLoading(false);
                }
            }
        };

        setImageFailed(false);
        setthumbnailLoading(true);
        loadThumbnail();

        return () => {
            isMounted = false;
        };
    }, [video, props.apiClient]);

    useEffect(() => {
        setIsFavourite(!!video.isFavourite);
    }, [video]);

    const handleDeleteVideo = async () => {
        if (!await confirmAction("Are you sure you wish to delete this video?")) return;
        setUpdating(true);
        try {
            await props.apiClient.videosDELETE(video.id!);
            props.onVideoUpdate?.(video);
        } catch (e) {
            console.error(e);
        } finally {
            setUpdating(false);
        }
    }

    const handleDeleteTag = (tag: string) => {
        if (onToggleTag) {
            onToggleTag(new ToggleVideoTagBindingModel({ name: tag, action: TagAction._0 }));
        }
    }

    const handleTagSubmit = async (tagName: string) => {
        if (onToggleTag) {
            onToggleTag(new ToggleVideoTagBindingModel({ name: tagName, action: TagAction._1 }));
        }
    };

    const handleRescanClick = async () => {
        if (!await confirmAction("Are you sure you wish to rescan this video?")) return;
        setUpdating(true);
        try {
            await props.apiClient.rescan(video.id!);
            props.onVideoUpdate?.(video);
        } catch (e) {
            console.error(e);
        } finally {
            setUpdating(false);
        }
    }

    const handleFavouriteClick = async (e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setUpdating(true);
        try {
            const nextValue = !isFavourite;
            const updated = await props.apiClient.toggleFavourite(video.id!, new BooleanBindingModel({ value: nextValue }));
            setIsFavourite(!!updated.isFavourite);
            props.onVideoUpdate?.(updated);
        } catch (e) {
            console.error(e);
        } finally {
            setUpdating(false);
        }
    };

    const formattedDuration = formatDuration(video.durationInSeconds);

    return (
        <Card className="video-result h-100">
            {onToggleTag && (
                <AddTagModal
                    show={showModal}
                    onHide={() => setShowModal(false)}
                    onSubmit={handleTagSubmit}
                    availableTags={props.tags}
                />
            )}

            <div className="video-result__media-wrapper">
                <a href={video.url!} target="_blank" rel="noreferrer noopener" className="video-result__media-link">
                    {thumbnailLoading ? (
                        <div className="video-result__skeleton" aria-label="Loading thumbnail" />
                    ) : imageFailed || !thumbnailBase64 ? (
                        <div className="video-result__image video-result__image--placeholder d-flex flex-column align-items-center justify-content-center gap-2">
                            <FontAwesomeIcon icon={faImage} size="2x" className="opacity-50" />
                            <span className="small text-body-secondary">No thumbnail</span>
                        </div>
                    ) : (
                        <img
                            className="video-result__image"
                            src={thumbnailBase64}
                            alt={video.title!}
                            onError={() => setImageFailed(true)}
                        />
                    )}

                    <div className="video-result__play-overlay" aria-hidden="true">
                        <div className="video-result__play-btn">
                            <FontAwesomeIcon icon={faPlay} />
                        </div>
                    </div>
                </a>

                <button
                    type="button"
                    className={`video-result__fav-overlay ${isFavourite ? 'video-result__fav-overlay--active' : ''}`}
                    onClick={handleFavouriteClick}
                    disabled={updating}
                    aria-label={isFavourite ? "Unfavourite" : "Mark as favourite"}
                    title={isFavourite ? "Unfavourite" : "Mark as favourite"}
                >
                    <FontAwesomeIcon icon={isFavourite ? faSolidStar : faRegStar} />
                </button>

                {formattedDuration && (
                    <span className="video-result__duration-badge">
                        <FontAwesomeIcon icon={faClock} className="me-1" />
                        {formattedDuration}
                    </span>
                )}

                {video.provider && (
                    <span className="video-result__provider-badge">
                        {video.provider}
                    </span>
                )}
            </div>

            <Card.Body className="video-result__body">
                <Card.Title className="video-result__title">
                    <a href={video.url!} target="_blank" rel="noreferrer noopener" title={video.title ?? ''}>
                        {video.title}
                    </a>
                </Card.Title>

                <Stack gap={2} className="mt-auto">
                    <Stack direction="horizontal" gap={2} className="flex-wrap">
                        {video.categoryName ? (
                            <Badge
                                as={Link}
                                to={`/search?categoryName=${encodeURIComponent(video.categoryName)}`}
                                bg="primary-subtle"
                                className="text-primary-emphasis text-decoration-none"
                                title={`Search videos in ${video.categoryName}`}
                            >
                                {video.categoryName}
                            </Badge>
                        ) : (
                            <Badge bg="primary-subtle" className="text-primary-emphasis">
                                {uncategorizedText}
                            </Badge>
                        )}
                    </Stack>

                    {video.tags && video.tags.length > 0 && (
                        <Stack direction="horizontal" gap={1} className="flex-wrap">
                            {video.tags.map((tag, index) => (
                                <Badge
                                    key={index}
                                    bg="secondary-subtle"
                                    className="text-secondary-emphasis d-inline-flex align-items-center gap-1 py-1 px-2 fw-normal"
                                >
                                    <FontAwesomeIcon icon={faTag} size="xs" />
                                    <Link
                                        to={`/search?tag=${encodeURIComponent(tag)}`}
                                        className="text-reset text-decoration-none"
                                        title={`Search videos tagged ${tag}`}
                                    >
                                        {tag}
                                    </Link>
                                    <span
                                        role="button"
                                        className="text-body-secondary ms-1"
                                        aria-label={`Remove tag ${tag}`}
                                        onClick={() => handleDeleteTag(tag)}
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </span>
                                </Badge>
                            ))}
                        </Stack>
                    )}

                    {((video.retries ?? 0) > 0) && (
                        <Badge bg="danger-subtle" className="text-danger-emphasis align-self-start py-1 px-2">
                            <Stack direction="horizontal" gap={1}>
                                <FontAwesomeIcon icon={faSync} />
                                <span>Retries: {video.retries}</span>
                            </Stack>
                        </Badge>
                    )}

                    <div className="d-flex justify-content-between align-items-center pt-2 border-top">
                        <DropdownButton
                            id={`video-actions-dropdown-${video.id}`}
                            title={<><FontAwesomeIcon icon={faEllipsisVertical} className="me-1" />Actions</>}
                            variant="outline-secondary"
                            size="sm"
                            disabled={updating}
                        >
                            <LinkContainer to={`/videos/${video.id}/edit`}>
                                <Dropdown.Item>
                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                    Edit
                                </Dropdown.Item>
                            </LinkContainer>
                            <Dropdown.Item as="button" onClick={handleRescanClick} disabled={updating}>
                                <FontAwesomeIcon icon={faSync} className="me-2" />
                                Rescan
                            </Dropdown.Item>
                            <Dropdown.Item as="button" onClick={() => setShowModal(true)}>
                                <FontAwesomeIcon icon={faTag} className="me-2" />
                                Add tag
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                                as="button"
                                onClick={handleDeleteVideo}
                                disabled={updating}
                                className="text-danger"
                            >
                                <FontAwesomeIcon icon={faTrash} className="me-2" />
                                Delete
                            </Dropdown.Item>
                        </DropdownButton>

                        <Button
                            variant={isFavourite ? "warning" : "outline-secondary"}
                            size="sm"
                            onClick={handleFavouriteClick}
                            disabled={updating}
                            title={isFavourite ? "Unfavourite" : "Mark as favourite"}
                            aria-label={isFavourite ? "Unfavourite" : "Mark as favourite"}
                        >
                            <FontAwesomeIcon icon={isFavourite ? faSolidStar : faRegStar} />
                        </Button>
                    </div>
                </Stack>
            </Card.Body>
        </Card>
    );
};
